import { Img } from '@shared/Frontend/components/img'
import { Video } from '@shared/Frontend/components/video'
import { useInView } from '@shared/Frontend/hooks/useInView'
import { cn } from '@shared/Frontend/utils/addToClassName'
import { slugify } from '@shared/Frontend/utils/slugify'
import * as React from 'react'
import { MediaBlock } from '../../blocks.state'

export const MediaBlockLayout = (
  props: MediaBlock & { id: string; videoCookieAccepted: boolean; cookiebarElement?: JSX.Element }
) => {
  const { id, label, title, intro, primaryUpdater, secondaryUpdater, showUpdaterAsLink } = props

  const { inView, ref } = useInView({ threshold: 0.1 })
  const hasPrimaryUpdater = primaryUpdater && primaryUpdater.title && primaryUpdater.url
  const hasSecondaryUpdater = secondaryUpdater && secondaryUpdater.title && secondaryUpdater.url
  return (
    <section id={id} ref={ref} className={cn('block block--media ls-block-spacing', inView && 'inview')}>
      <div className="block__inner ls-container">
        <div className="block__layout ls-columns">
          {(label || title || intro) && (
            <header className="block__header ls-columns__header">
              <div className="block__header-inner ls-reversed-column">
                {title && (
                  <h2 id={slugify(title)} className="block__title title--l">
                    {title}
                  </h2>
                )}
                {label && <p className="block__label">{label}</p>}
              </div>
              {intro && <p className="block__intro">{intro}</p>}
            </header>
          )}

          <div className="block__content ls-columns__main-wide">
            <ListLayout {...props} itemClassName="block__figure" />
          </div>

          {/* NB: Van Oord currently doesn't use block links in the media block  */}
          {/* {(hasPrimaryUpdater || hasSecondaryUpdater) && (
          <footer className="block__footer">
            <div className="ls-clickables">
              {hasPrimaryUpdater && (
                <div className="ls-clickable">
                  <Clickable
                    elementKind="link"
                    styleKind={showUpdaterAsLink ? 'link' : 'button'}
                    {...primaryUpdater}
                    className={
                      showUpdaterAsLink
                        ? 'block__clickable block__clickable--secondary'
                        : 'block__clickable block__clickable--primary'
                    }
                    onClick={() =>
                      pushDatalayer({
                        category: 'MediaBlock',
                        action: primaryUpdater.title,
                      })
                    }
                  />
                </div>
              )}
              {hasSecondaryUpdater && (
                <div className="ls-clickable">
                  <Clickable
                    elementKind="link"
                    styleKind="link"
                    {...secondaryUpdater}
                    className="block__clickable block__clickable--secondary"
                    onClick={() =>
                      pushDatalayer({
                        category: 'MediaBlock',
                        action: secondaryUpdater.title,
                      })
                    }
                  />
                </div>
              )}
            </div>
          </footer>
        )} */}
        </div>
      </div>
    </section>
  )
}

export const ListLayout = (props: {
  items: MediaBlock['items']
  videoCookieAccepted: boolean
  cookiebarElement?: JSX.Element
  itemClassName?: string | ((displayOption: MediaBlock['items'][number]['displayOption']) => string)
}) => {
  if (!props.items || props.items.length == 0) return <></>
  return (
    <>
      {props.items.map((item, index) => (
        <ItemLayout key={`media-${index}`} {...item} {...props} />
      ))}
    </>
  )
}

export const ItemLayout = (
  props: {
    videoCookieAccepted: boolean
    cookiebarElement?: JSX.Element
    itemClassName?: string | ((displayOption: MediaBlock['items'][number]['displayOption']) => string)
  } & MediaBlock['items'][number]
) => {
  const { itemClassName, displayOption, mediaType, caption, source } = props
  const itemClass = itemClassName
    ? typeof itemClassName == 'string'
      ? itemClassName
      : itemClassName(displayOption)
    : ''
  const { inView, ref } = useInView({ threshold: 0.2 })
  const isCW = displayOption == 'content-width'
  return (
    <figure
      ref={ref}
      className={cn(
        `figure--${displayOption == 'content-width' ? 'cw' : 'fw'} figure--${mediaType}`,
        itemClass,
        'ls-columns',
        inView && 'inview'
      )}
    >
      {props.mediaType == 'image' && props.image ? (
        <Img
          {...props.image}
          loading="lazy"
          className={cn('figure__image', isCW ? 'ls-columns__main' : ' ls-columns__main-wide')}
          // 16:9 to reserve some space
          width={displayOption == 'content-width' ? '1076' : '1440'}
          height={displayOption == 'content-width' ? '605' : '810'}
          sizes={displayOption == 'content-width' ? '(min-width: 1440px) 1076px, 100vw' : '100vw'}
        />
      ) : props.mediaType == 'video' && props.url ? (
        <Video
          {...props}
          url={props.url}
          className={cn('figure__video', isCW ? 'ls-columns__main' : ' ls-columns__main-wide')}
        />
      ) : (
        <></>
      )}

      {(caption || source) && (
        <figcaption className={cn('figure__figcaption', isCW ? 'ls-columns__sidebar' : 'ls-columns__tail')}>
          {caption && <span className={cn('figure__caption')} dangerouslySetInnerHTML={{ __html: caption }} />}
          {source && <cite className="figure__source" dangerouslySetInnerHTML={{ __html: ` — ${source}` }} />}
        </figcaption>
      )}
    </figure>
  )
}
